import { Workflow, Lock, Zap, BarChart3, Clock, Shield } from "lucide-react";

const Features = () => {
  const automationFeatures = [
    {
      icon: Workflow,
      title: "Fluxos Inteligentes",
      description: "Crie automações complexas com interface visual intuitiva. Arraste, solte e conecte.",
      color: "primary",
    },
    {
      icon: Zap,
      title: "Execução Instantânea",
      description: "Trigger em tempo real para automações que respondem imediatamente às mudanças.",
      color: "secondary",
    },
    {
      icon: Clock,
      title: "Agendamento Flexível",
      description: "Programe suas automações para executar no momento exato que você precisa.",
      color: "accent",
    },
  ];

  const securityFeatures = [
    {
      icon: Lock,
      title: "Criptografia Avançada",
      description: "Seus dados protegidos com criptografia de ponta a ponta em todas as comunicações.",
      color: "primary",
    },
    {
      icon: Shield,
      title: "Compliance Total",
      description: "Conformidade com LGPD, GDPR e principais regulamentações de segurança.",
      color: "secondary",
    },
    {
      icon: BarChart3,
      title: "Monitoramento 24/7",
      description: "Logs completos e monitoramento em tempo real de todas as suas automações.",
      color: "accent",
    },
  ];

  return (
    <>
      {/* Seção de Automação */}
      <section id="automacao" className="py-24 relative">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              <span className="text-gradient">Automação Inteligente</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Automatize processos complexos sem escrever uma linha de código. Visual, poderoso e escalável.
            </p>
          </div>

          <div className="relative overflow-hidden py-4">
            <div className="flex gap-8 w-max animate-scroll-right-slow pause-on-hover">
              {/* Duplicando para loop (4x pois são poucos itens) */}
              {[...automationFeatures, ...automationFeatures, ...automationFeatures, ...automationFeatures].map((feature, index) => (
                <div key={index} className="w-[400px] flex-shrink-0">
                  <FeatureCard {...feature} />
                </div>
              ))}
            </div>
            <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
            <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
          </div>
        </div>
      </section>

      {/* Seção de Segurança */}
      <section id="seguranca" className="py-24 relative bg-card/50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              <span className="text-gradient">Segurança em Primeiro Lugar</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Enterprise-grade security para proteger seus dados e processos críticos de negócio.
            </p>
          </div>

          <div className="relative overflow-hidden py-4">
            <div className="flex gap-8 w-max animate-scroll-right-slow pause-on-hover">
              {/* Duplicando para loop (4x pois são poucos itens) */}
              {[...securityFeatures, ...securityFeatures, ...securityFeatures, ...securityFeatures].map((feature, index) => (
                <div key={index} className="w-[400px] flex-shrink-0">
                  <FeatureCard {...feature} />
                </div>
              ))}
            </div>
            <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-card to-transparent z-10 pointer-events-none" />
            <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-card to-transparent z-10 pointer-events-none" />
          </div>
        </div>
      </section>
    </>
  );
};

const FeatureCard = ({ icon: Icon, title, description, color }: {
  icon: any;
  title: string;
  description: string;
  color: string;
}) => {
  const colorMap = {
    primary: "border-primary/30 hover:border-primary text-primary",
    secondary: "border-secondary/30 hover:border-secondary text-secondary",
    accent: "border-accent/30 hover:border-accent text-accent",
  };

  return (
    <div className={`group p-8 rounded-2xl border-2 ${colorMap[color as keyof typeof colorMap]} bg-card/50 backdrop-blur-sm transition-all duration-300 hover:scale-105 hover:shadow-2xl`}>
      <div className="mb-6 inline-flex p-4 rounded-xl bg-background/50">
        <Icon className="w-8 h-8" />
      </div>
      <h3 className="text-2xl font-bold mb-4 text-foreground">{title}</h3>
      <p className="text-muted-foreground leading-relaxed">{description}</p>
    </div>
  );
};

export default Features;
